(function () {
  /** 使用方式：
    * <custom-select class="custom-select" active-value="当前选择的值">
    *   <div class="custom-select-label" data-custom-select-label>当前值对应显示文本</div>
    *   <span class="arrow-out">
    *     {% render 'icons', name: 'dropdown-arrow' %}
    *   </span>
    *   <div class="custom-select-options" data-custom-select-options>
    *     {%- for 列表项 in 列表 -%}
    *       <custom-select-option class="custom-select-option" value="列表项的值">
    *         列表项的值对应的显示文本
    *       </custom-select-option>
    *     {%- endfor -%}
    *   </div>
    *   <select>可选，适配原生标签</select>
    * </custom-select>
    * 
    * 支持事件监听：'change'
    * 无选项时，自动读取内内部原生 <select> 标签（若有）内的 option
    * 
    * Attribute:
    * @param {data-use-fixed-options} 为 true 时启用悬浮定位模拟 options 代替绝对定位
   */
  class CustomSelect extends HTMLElement {
    static get observedAttributes() {
      return ['active-value']
    }

    constructor() {
      super();
      this.optionsWrapper = this.querySelector('[data-custom-select-options]');
      this.optionElements = this.querySelectorAll('custom-select-option');
      this.selectLabel = this.querySelector("[data-custom-select-label]");
      this.value = this.getAttribute("active-value");

      // 适配原生 select
      this.selectElement = this.querySelector("select");
      if (this.selectElement) {
        this.useNativeSelect = true;
      }

      // 无选项时，读取原生 option
      if (this.optionElements.length == 0 && this.useNativeSelect) {
        this.renderFromNativeSelect();
      }
    }

    connectedCallback() {
      this.render(this.value);
      this.initEvent();
      if (this.getAttribute('data-use-fixed-options') == 'true') {
        this.initFixedOptionsFrame();
      }
    }

    disconnectedCallback() {
      document.removeEventListener("click", this._hideList);
      this.removeEventListener('click', this._flipArrow);
      this.removeEventListener('click', this._handleClick.bind(this));

      // this.optionElements
      //   .forEach((item, index) => {
      //     item.removeEventListener('click', this.change.bind(this, item.getAttribute("value")));
      //   });
    }

    attributeChangedCallback(attr, oldVal, newVal) {
      const attribute = attr.toLowerCase()
      if (attribute === 'active-value') {
        this.render(newVal);
      }
    }

    initEvent() {
      this._hideList = this.hideList.bind(this);
      this._flipArrow = this.flipArrow.bind(this);
      this._handleClick = (e) => {
        if (e.target?.tagName.toLowerCase() == 'custom-select-option') {
          this.change(e.target.getAttribute("value"));
        }
      };

      document.addEventListener("click", this._hideList);
      this.addEventListener('click', this._flipArrow);
      this.addEventListener('click', this._handleClick.bind(this));
      if (this.useNativeSelect && this.selectElement) {
        // 选项列表跟随原生 select options 变化
        const targetNode = this.selectElement;
        const config = {
          childList: true,
          subtree: true,
        };
        const callback = (mutationsList, _) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'childList') {
              this.renderFromNativeSelect();
            }
          }
        };
        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);
      }
    }

    change(value) {
      this.setAttribute('active-value', value);
      let optionLabel = undefined;

      const optionElement = this.querySelector(`custom-select-option[value="${value}"]`);
      if (optionElement) {
        optionLabel = optionElement.querySelector('[data-option-value]')?.innerHTML ?? optionElement.innerHTML;
      }

      let changeInfo = {
        detail: {
          value: value,
          label: optionLabel.trim(),
          item: optionElement
        },
        bubbles: true
      }
      let changeEvent = new CustomEvent('change', changeInfo)
      this.dispatchEvent(changeEvent);

      if (this.selectElement) {
        this.selectElement.value = value;
        this.selectElement.dispatchEvent(new CustomEvent('change'));
      }
    }

    flipArrow(e) {
      const other = document.querySelector("custom-select.active");
      if (other && other != this) {
        other.hideList();
      }
      if (!this._arrowFlip) {
        this.showList()
      } else {
        this.hideList()
      }
      e.stopPropagation();
    }

    showList() {
      this._arrowFlip = true
      if (!this.classList.contains('active')) {
        this.classList.add("active");
        this.dispatchEvent(new CustomEvent('show'));
      }
    }

    hideList() {
      this._arrowFlip = false;
      if (this.classList.contains('active')) {
        this.dispatchEvent(new CustomEvent('hide'));
        this.classList.remove("active");
      }
    }

    blurWrapper() {
      this.addEventListener('blur', (event) => {
        event.stopPropagation()
        this.hideList()
      })
    }

    render(value) {
      const optionElement = this.querySelector(`custom-select-option[value="${value}"]`);
      if (optionElement) {
        const optionLabel = optionElement.querySelector('[data-option-value]')?.innerHTML ?? optionElement.innerHTML;
        if (!this.selectLabel) {
          this.selectLabel = this.querySelector("[data-custom-select-label]");
        }
        this.selectLabel.innerHTML = optionLabel;
        this.value = value;
        this.querySelector("[checked]")?.removeAttribute("checked");
        optionElement.setAttribute("checked", "");
      }
    }

    renderFromNativeSelect() {
      const value = this.selectElement.value;
      let innerHTML = '';
      for (const option of this.selectElement.options) {
        innerHTML += `<custom-select-option class="custom-select-option" value="${option.value}">${option.label}</custom-select-option>`;
      }
      this.optionsWrapper.innerHTML = innerHTML;
      this.render(value);
    }

    // 'position: fixed' for options
    initFixedOptionsFrame() {
      this.addEventListener('click', (event) => {
        this.setFramePosition();
      });

      window.addEventListener('scroll', (event) => {
        if (this.classList.contains('active')) {
          this.setFramePosition();
        }
      });
    }

    setFramePosition() {
      const bounding = this.getBoundingClientRect();
      const positionX = bounding.left;
      const positionY = bounding.top + bounding.height;
      this.style.setProperty('--position-x', positionX + 'px');
      this.style.setProperty('--position-y', positionY + 'px');
      this.style.setProperty('--parent-width', bounding.width + 'px');
    }
  }

  const FrozenCustomSelect = Object.freeze(CustomSelect);
  customElements.define('custom-select', FrozenCustomSelect);
})()
